import { FC, useEffect, useState } from "react";
import DiscardConfirmationModal from "../../common/discard-confirmation-modal/DiscardConfirmationModal";
import { Box, Typography, CircularProgress, MenuItem, Select, FormControl, FormHelperText, InputLabel } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
    createPriceOverrideConfigAction,
    updatePriceOverrideConfigAction,
    getAllPriceOverrideConfigsAction,
} from "../../../redux/actions/priceOverrideConfigAction";
import { getCategoryAction } from "../../../redux/actions/pricingAction";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { ReduceAmountType } from "../../../typings/enum/ReduceAmountType";
import { IPriceOverrideConfigResponse } from "../../../typings/interfaces/responses/priceOverrideConfigResponse";

const schema = Yup.object().shape({
    categoryId: Yup.number().required("Category is required"),
    slabMinPrice: Yup.number().required("Min price is required").min(0, "Must be >= 0"),
    slabMaxPrice: Yup.number().required("Max price is required").moreThan(Yup.ref('slabMinPrice'), "Max price must be greater than min price"),
    reduceAmountType: Yup.mixed<ReduceAmountType>().oneOf(Object.values(ReduceAmountType)).required("Reduction type is required"),
    reduceAmount: Yup.number().required("Reduction amount is required").min(0, "Must be >= 0"),
    processingType: Yup.string().required("Processing type is required"),
});

export interface ICreatePriceOverrideDrawerProps {
    open: boolean;
    toggleDrawer: () => void;
    populateFor?: IPriceOverrideConfigResponse;
    selectedCategoryId?: number | null;
}

export const AddPriceOverrideDrawer: FC<ICreatePriceOverrideDrawerProps> = ({
    open,
    toggleDrawer,
    populateFor,
    selectedCategoryId,
}) => {
    const dispatch = useAppDispatch();
    const snackbar = useSnackbarContext();

    const { addConfigLoading, updateConfigLoading } = useAppSelector(
        (state) => state.priceOverrideConfig
    );

    const { categoryResponse, categoryResponseLoading } = useAppSelector(
        (state) => state.pricing
    );
    // Filter categories similar to POS Check-in page
    const categoryiesList = categoryResponse?.body?.filter(tab => tab.showInMenu) || [];
    // The POS Check-in also adds an explicit "Alteration" category
    const alterationCategory = { id: -100000, categoryName: "Alteration" };
    const displayCategories = [...categoryiesList, alterationCategory];
    const categoryiesLoading = categoryResponseLoading;

    const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

    const { control, handleSubmit, reset, formState: { isDirty } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            categoryId: 0,
            slabMinPrice: 0,
            slabMaxPrice: 0,
            reduceAmountType: ReduceAmountType.FIXED,
            reduceAmount: 0,
            processingType: "ALL",
        },
    });

    useEffect(() => {
        if (open) {
            dispatch(getCategoryAction());
            if (populateFor) {
                reset({
                    categoryId: populateFor.categoryId,
                    slabMinPrice: populateFor.slabMinPrice,
                    slabMaxPrice: populateFor.slabMaxPrice,
                    reduceAmountType: populateFor.reduceAmountType,
                    reduceAmount: populateFor.reduceAmount,
                    processingType: populateFor.processingType || "ALL",
                });
            } else {
                reset({
                    categoryId: selectedCategoryId || 0,
                    slabMinPrice: 0,
                    slabMaxPrice: 0,
                    reduceAmountType: ReduceAmountType.FIXED,
                    reduceAmount: 0,
                    processingType: "ALL",
                });
            }
        }
    }, [open, populateFor, reset, dispatch, selectedCategoryId]);

    const handleSaveConfig = async (data: any) => {
        const payload = {
            categoryId: data.categoryId,
            slabMinPrice: data.slabMinPrice,
            slabMaxPrice: data.slabMaxPrice,
            reduceAmountType: data.reduceAmountType,
            reduceAmount: data.reduceAmount,
            processingType: data.processingType as "ALL" | "PRESS_ONLY" | "CLEAN_ONLY",
        };

        try {
            if (populateFor?.id) {
                await dispatch(
                    updatePriceOverrideConfigAction({ ...payload, id: populateFor.id })
                ).unwrap();
                snackbar.showSnackbar("Config updated successfully!", "success");
            } else {
                await dispatch(createPriceOverrideConfigAction(payload)).unwrap();
                snackbar.showSnackbar("Config added successfully!", "success");
            }
            dispatch(getAllPriceOverrideConfigsAction());
            toggleDrawer();
        } catch (error) {
            snackbar.showSnackbar(
                "Failed to save config. Please try again.",
                "error"
            );
        }
    };

    const handleCloseAttempt = () => {
        if (isDirty) {
            setExitConfirmOpen(true);
        } else {
            toggleDrawer();
        }
    };

    return (
        <>
            <DiscardConfirmationModal
                open={exitConfirmOpen}
                onKeepEditing={() => setExitConfirmOpen(false)}
                onDiscard={() => { reset(); setExitConfirmOpen(false); toggleDrawer(); }}
            />
            <CustomDrawer
                isOpen={open}
                toggleDrawer={handleCloseAttempt}
                sx={{ width: "32rem" }}
            >
                <form onSubmit={handleSubmit(handleSaveConfig)}>
                    <Box
                        sx={{
                            paddingInline: "1.857rem",
                            paddingTop: "2rem",
                            display: "flex",
                            flexDirection: "column",
                            gap: "1.5rem",
                            pb: "2rem"
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: "bold",
                                fontSize: "1.4rem",
                                lineHeight: "1.7rem",
                                color: Colors.TEXT_GRAY_DARK,
                                mb: "1rem"
                            }}
                        >
                            {populateFor ? "Update Price Override" : "Add a New Price Override"}
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <Typography>Category</Typography>
                            <Controller
                                name="categoryId"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <FormControl fullWidth error={fieldState.invalid}>
                                        {categoryiesLoading ? <CircularProgress size={20} /> : (
                                            <Select
                                                {...field}
                                                sx={{ backgroundColor: Colors.WHITE, height: "3rem" }}
                                            >
                                                <MenuItem value={0} disabled>Select Category</MenuItem>
                                                {displayCategories.map((cat: any) => (
                                                    <MenuItem key={cat.id} value={cat.id}>{cat.categoryName}</MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <Typography>Slab Min Price</Typography>
                            <Controller
                                name="slabMinPrice"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <CustomTextBox
                                        {...field}
                                        type="number"
                                        sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                                        placeholder="Enter min price"
                                        helperText={fieldState.error?.message}
                                        error={fieldState.invalid}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <Typography>Slab Max Price</Typography>
                            <Controller
                                name="slabMaxPrice"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <CustomTextBox
                                        {...field}
                                        type="number"
                                        sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                                        placeholder="Enter max price"
                                        helperText={fieldState.error?.message}
                                        error={fieldState.invalid}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <Typography>Reduce Amount Type</Typography>
                            <Controller
                                name="reduceAmountType"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <FormControl fullWidth error={fieldState.invalid}>
                                        <Select
                                            {...field}
                                            sx={{ backgroundColor: Colors.WHITE, height: "3rem" }}
                                        >
                                            <MenuItem value={ReduceAmountType.FIXED}>Fixed</MenuItem>
                                            <MenuItem value={ReduceAmountType.PERCENTAGE}>Percentage</MenuItem>
                                        </Select>
                                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <Typography>Processing Type</Typography>
                            <Controller
                                name="processingType"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <FormControl fullWidth error={fieldState.invalid}>
                                        <Select
                                            {...field}
                                            sx={{ backgroundColor: Colors.WHITE, height: "3rem" }}
                                        >
                                            <MenuItem value="ALL">Applicable for All</MenuItem>
                                            <MenuItem value="PRESS_ONLY">Press Only</MenuItem>
                                            <MenuItem value="CLEAN_ONLY">Clean Only</MenuItem>
                                        </Select>
                                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <Typography>Reduce Amount</Typography>
                            <Controller
                                name="reduceAmount"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <CustomTextBox
                                        {...field}
                                        type="number"
                                        sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                                        placeholder="Enter reduce amount"
                                        helperText={fieldState.error?.message}
                                        error={fieldState.invalid}
                                    />
                                )}
                            />
                        </Box>

                        <CustomButton
                            sx={{ width: "10rem", height: "2.6rem", marginTop: "1rem" }}
                            type="submit"
                            disabled={addConfigLoading || updateConfigLoading}
                        >
                            {addConfigLoading || updateConfigLoading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : populateFor ? (
                                "Update Config"
                            ) : (
                                "Add Config"
                            )}
                        </CustomButton>
                    </Box>
                </form>
            </CustomDrawer>
        </>
    );
};
